import { LikeFilled, LikeOutlined } from "@ant-design/icons"
import { Button, message, Popconfirm, DatePicker, Select } from "antd"
import { doc, serverTimestamp, updateDoc } from "firebase/firestore"
import React, { useState } from "react"
import { db } from "../../../firebase/config"
import { useAuthContext } from "../../../hooks/useAuthContext"
import confetti from "canvas-confetti"
import moment from "moment"
import { updateProductOrdersArray } from "../../../utils/updateProductOrdersArray"
import { handleCalculate } from "../../Calculation/handleCalculate"
import { cleanupVivaPlate } from "../../../utils/cleanupVivaPlate"
import { cleanupTapePlate } from "../../../utils/cleanupTapePlate"

const { Option } = Select

export default function MarkAsDone({ order, onDone, doneDate }) {
  const { user } = useAuthContext()
  const [buttonClicked, setButtonClicked] = useState(false)
  // Use the doneDate passed from the parent or default to today
  const [selectedDate, setSelectedDate] = useState(doneDate ? moment(doneDate) : moment())
  // State for the destination selection
  const [destination, setDestination] = useState("doneList")

  // Updated order complete handler
  const handleCompleteOrder = async () => {
    setButtonClicked(true)
    runConfetti(0.5, 0.5)

    setTimeout(async () => {
      const orderRef = doc(db, "orders", order.id)
      const priceRef = doc(db, "priceCalculation", order.selectedProduct.id)

      try {
        // 1. Run the calculation first
        let systemPrice = null
        try {
          const calcResult = await handleCalculate(
            { ...order.selectedProduct, user: order.user },
            order.labelCount,
          )
          systemPrice = calcResult.cenaPar1000uzlimemEur
        } catch (calcError) {
          console.error("Price calculation failed:", calcError)
          // non-fatal — we still proceed with marking as done
        }

        if (order.selectedPlate && order.selectedPlate.plateStatus === "new") {
          await updateDoc(orderRef, { "selectedPlate.plateStatus": "used" })

          // Arsoma (and others): mark as used in their plate list
          if (order.selectedProduct.machine !== "Viva") {
            const plateDocRef = doc(db, "arsomaPlateList", order.selectedPlate.id)
            await updateDoc(plateDocRef, { plateStatus: "used" })
          }
        }

        // Clean up plates for Viva and Tape machines
        await Promise.all([cleanupVivaPlate(order), cleanupTapePlate(order)])

        const orderStatus = destination === "ourCourier" ? 6.5 : 7

        // 2. Include the system price fields in the update
        await updateDoc(orderRef, {
          completedTime: serverTimestamp(),
          completedBy: user.uid,
          warehouseDoneDate: selectedDate.toDate(),
          completedByEmail: user.email,
          orderStatus: orderStatus,
          hasSystemPrice: systemPrice !== null,
          systemPriceForOrder: systemPrice,
        })

        await updateDoc(priceRef, { hasDoneOrders: true })
        await updateProductOrdersArray("remove", order)

        message.success(
          destination === "ourCourier" ? "Order sent to Our Courier" : "Order marked as done",
        )
      } catch (err) {
        message.error(err.message)
      }
    }, 500)
    onDone()
  }

  const runConfetti = (originX, originY) => {
    confetti({
      angle: 90,
      spread: 50,
      particleCount: 30,
      origin: { x: originX, y: originY },
      startVelocity: 15,
      gravity: 0.5,
      duration: 1000,
    })
  }

  // Build the content for the popconfirm with both DatePicker and Select
  const popconfirmContent = (
    <div style={{ minWidth: 250 }}>
      <div style={{ marginBottom: 12 }}>
        <p style={{ marginBottom: 4, fontWeight: 500 }}>Select completion date:</p>
        <DatePicker
          value={selectedDate}
          onChange={(date) => setSelectedDate(date)}
          defaultValue={moment()}
          style={{ width: "100%" }}
        />
      </div>
      <div>
        <p style={{ marginBottom: 4, fontWeight: 500 }}>Send out to:</p>
        <Select
          value={destination}
          onChange={(value) => setDestination(value)}
          style={{ width: "100%" }}
          defaultValue="doneList">
          <Option value="doneList">Done List</Option>
          <Option value="ourCourier">Our Courier</Option>
        </Select>
      </div>
    </div>
  )

  return (
    <Popconfirm
      title={popconfirmContent}
      onConfirm={handleCompleteOrder}
      okText="Confirm"
      cancelText="Cancel">
      <Button type="link" icon={buttonClicked ? <LikeFilled /> : <LikeOutlined />} />
    </Popconfirm>
  )
}
